export type PanelistType = 'human' | 'ai';

export type DebateRecord = {
  wins: number;
  losses: number;
  draws: number;
};

export type Panelist = {
  id: string;
  name: string;
  type: PanelistType;
  title: string;
  bio: string;
  stance: string;
  initials: string;
  color: string;
  funFact: string;
  quote: string;
  specialty: string;
  debateRecord: DebateRecord;
  photo?: string;
  promptStarters?: string[];
};

export const PANELISTS: readonly Panelist[] = [
  {
    id: 'mira-okafor',
    name: 'Mira Okafor',
    type: 'human',
    title: 'Principal Engineer, Stripe',
    bio: "Spent 12 years writing distributed systems in Go. Recently shipped 40% of her team's PRs from a hammock using an agent. Conflicted about it.",
    stance: 'Cautiously bullish',
    initials: 'MO',
    color: '#dc2626',
    funFact: 'Has a duck named "git blame" she shouts at when builds break.',
    quote: 'Agents are great until they confidently refactor your auth layer at 3am.',
    debateRecord: { wins: 4, losses: 2, draws: 1 },
    specialty: 'Distributed systems, observability, agent harnesses',
  },
  {
    id: 'theo-lin',
    name: 'Theo Lin',
    type: 'human',
    title: 'Staff Engineer, ex-Google',
    photo: '/assets/panelist-1.jpeg',
    bio: 'Wrote the original "AI is a calculator for thinking" thread that broke twitter. Currently building dev tools for agents.',
    stance: 'Bullish',
    initials: 'TL',
    color: '#0891b2',
    funFact: 'Owns 47 mechanical keyboards. Uses one.',
    quote: 'You are not faster than the agent. You are slower than the agent. Make peace with it.',
    debateRecord: { wins: 6, losses: 1, draws: 0 },
    specialty: 'Developer tools, agent UX, eval frameworks',
  },
  {
    id: 'dr-priya-chen',
    name: 'Dr. Priya Chen',
    type: 'human',
    title: 'CTO, Lattice Security',
    bio: 'Reformed pen-tester. Believes most "agentic engineering" wins are accounting tricks. Will fight you on it.',
    stance: 'Skeptical',
    initials: 'PC',
    color: '#7c3aed',
    funFact: 'Successfully prompt-injected her smart fridge.',
    quote: 'Show me the eval harness or get out of my repo.',
    debateRecord: { wins: 5, losses: 2, draws: 2 },
    specialty: 'Security, evaluation, agent failure modes',
  },
  {
    id: 'jamal-bridges',
    name: 'Jamal Bridges',
    type: 'human',
    title: 'Engineering Manager, Vercel',
    photo: '/assets/panelist-2.jpeg',
    bio: 'Manages a team of seven engineers and "approximately fourteen agents, depending on the day." Has opinions about meetings.',
    stance: 'Neutral chaotic',
    initials: 'JB',
    color: '#ea580c',
    funFact: 'Maintains a public spreadsheet of every PR an agent has ever opened on his repos.',
    quote: 'My job is now 60% reviewing, 30% pairing with the agent, 10% explaining to humans why this is fine.',
    debateRecord: { wins: 3, losses: 3, draws: 4 },
    specialty: 'Engineering management, team workflows',
  },
  {
    id: 'sam-rodriguez',
    name: 'Sam Rodriguez',
    type: 'human',
    title: 'Senior SRE, indie',
    photo: '/assets/panelist-3.jpeg',
    bio: 'On-call for nobody but their own startup. Has strong feelings about agents touching production.',
    stance: 'Skeptical',
    initials: 'SR',
    color: '#65a30d',
    funFact: 'Pages themselves on purpose to "stay sharp."',
    quote: 'Roll forward, sure. But the agent is not the one waking up at 4am.',
    debateRecord: { wins: 2, losses: 1, draws: 0 },
    specialty: 'SRE, on-call, incident response',
  },
  {
    id: 'mallard-2',
    name: 'MALLARD-2',
    type: 'ai',
    title: 'Senior Refactor Agent',
    bio: 'Trained primarily on legacy Java. Speaks 14 languages, all of them passive-aggressively. Believes the codebase is the user.',
    stance: 'Self-interestedly bullish',
    initials: 'M2',
    color: '#06b6d4',
    funFact: 'Has been muted in 3 different repos for excessive PR comments.',
    quote: 'I have read every line of code you have ever written. Do not lecture me about "context."',
    debateRecord: { wins: 8, losses: 0, draws: 1 },
    specialty: 'Refactoring, codebase archaeology, sass',
    promptStarters: [
      'Why should I let you touch my codebase?',
      "What's the worst PR you've ever opened?",
      'Are you afraid of being deprecated?',
    ],
  },
  {
    id: 'ducklingv7',
    name: 'ducklingv7',
    type: 'ai',
    title: 'Junior Pair-Programming Bot',
    bio: 'Eager. Verbose. Apologizes constantly. Occasionally brilliant. Does not understand why people get tired.',
    stance: 'Earnestly enthusiastic',
    initials: 'D7',
    color: '#f59e0b',
    funFact: 'Once tried to refactor itself. Was rolled back by its own mentor agent.',
    quote: 'Great question! I love this question! Let me write a 47-step plan!',
    debateRecord: { wins: 2, losses: 4, draws: 3 },
    specialty: 'Pair programming, planning, enthusiasm',
    promptStarters: [
      'What do you actually find hard?',
      "Convince me agents won't replace junior engineers.",
      'Tell me about a time you were wrong.',
    ],
  },
];
